import {Inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {of} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {ClustersService} from '../../services/api/clusters/clusters.service';
import {ClusterRegionsVocabularyService} from '../../services/api/vocabulary/cluster/regions/cluster.regions.vocabulary.service';
import * as ClustersActions from './clusters.actions';
import {Cluster} from '../../services/api/clusters/cluster.model';
import {ClusterRegion} from '../../services/api/vocabulary/cluster/regions/cluster.region.vocabulary.model';
import {Router} from '@angular/router';

/**
 * Effects for handling clusters side effects
 */
@Injectable()
export class ClustersEffects {
  /**
   * Effect: Load all clusters
   */
  loadClusters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClustersActions.loadClusters),
      switchMap(() =>
        this.clustersService.getClusters().pipe(
          map((clusters: Cluster[]) =>
            ClustersActions.loadClustersSuccess({clusters})
          ),
          catchError((error) =>
            of(
              ClustersActions.loadClustersFailure({
                error: this.extractErrorMessage(error)
              })
            )
          )
        )
      )
    )
  );

  /**
   * Effect: Load a single cluster by ID
   */
  loadClusterById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClustersActions.loadClusterById),
      switchMap(({id}) =>
        this.clustersService.getCluster(id).pipe(
          map((cluster: Cluster) =>
            ClustersActions.loadClusterByIdSuccess({cluster})
          ),
          catchError((error) =>
            of(
              ClustersActions.loadClusterByIdFailure({
                id,
                error: this.extractErrorMessage(error)
              })
            )
          )
        )
      )
    )
  );

  /**
   * Effect: Create a new cluster
   */
  createCluster$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClustersActions.createCluster),
      switchMap(({payload}) =>
        this.clustersService.createCluster(payload).pipe(
          map((cluster: Cluster) =>
            ClustersActions.createClusterSuccess({cluster})
          ),
          catchError((error) =>
            of(
              ClustersActions.createClusterFailure({
                error: this.extractErrorMessage(error)
              })
            )
          )
        )
      )
    )
  );

  /**
   * Effect: Delete a cluster
   */
  deleteCluster$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClustersActions.deleteCluster),
      switchMap(({payload}) =>
        this.clustersService.deleteCluster(payload).pipe(
          map((cluster: Cluster) =>
            ClustersActions.deleteClusterSuccess({id: payload.clusterId, cluster})
          ),
          catchError((error) =>
            of(
              ClustersActions.deleteClusterFailure({
                id: payload.clusterId,
                error: this.extractErrorMessage(error)
              })
            )
          )
        )
      )
    )
  );

  /**
   * Effect: Start a cluster
   */
  startCluster$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClustersActions.startCluster),
      switchMap(({id}) =>
        this.clustersService.startCluster(id).pipe(
          map((cluster: Cluster) =>
            ClustersActions.startClusterSuccess({cluster})
          ),
          catchError((error) =>
            of(
              ClustersActions.startClusterFailure({
                id,
                error: this.extractErrorMessage(error)
              })
            )
          )
        )
      )
    )
  );

  /**
   * Effect: Stop a cluster
   */
  stopCluster$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClustersActions.stopCluster),
      switchMap(({id}) =>
        this.clustersService.stopCluster(id).pipe(
          map((cluster: Cluster) =>
            ClustersActions.stopClusterSuccess({cluster})
          ),
          catchError((error) =>
            of(
              ClustersActions.stopClusterFailure({
                id,
                error: this.extractErrorMessage(error)
              })
            )
          )
        )
      )
    )
  );

  /**
   * Effect: Load all cluster regions
   */
  loadClusterRegions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClustersActions.loadClusterRegions),
      switchMap(() =>
        this.clusterRegionsVocabularyService.getAll().pipe(
          map((regions: ClusterRegion[]) =>
            ClustersActions.loadClusterRegionsSuccess({regions})
          ),
          catchError((error) =>
            of(
              ClustersActions.loadClusterRegionsFailure({
                error: this.extractErrorMessage(error)
              })
            )
          )
        )
      )
    )
  );

  /**
   * Effect: Navigate to clusters list after successful creation
   */
  createClusterSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClustersActions.createClusterSuccess),
      tap(() => this.router.navigate(['/clusters'])),
    ),
    {dispatch: false}
  );

  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    @Inject(ClustersService) private readonly clustersService: ClustersService,
    @Inject(ClusterRegionsVocabularyService) private readonly clusterRegionsVocabularyService: ClusterRegionsVocabularyService
  ) {
  }

  /**
   * Helper method to extract an error message from an error object
   */
  private extractErrorMessage(error: any): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error?.error?.message) {
      return error.error.message;
    }

    if (error?.message) {
      return error.message;
    }

    if (error?.statusText) {
      return error.statusText;
    }

    return 'An unknown error occurred';
  }
}
